import React, { useState, useRef } from "react";
import axios from "axios";
import { Box, Button, Grid, Typography, TextField, IconButton, Chip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import DeleteIcon from "@mui/icons-material/Delete";
import { useSelector } from "react-redux";
import ReusableDialog from "../../../../common/dialog/dialog";

const ManagerKitchenChecklist = ({ onClose, setOpenDefaultSnackbar }) => {
  const [time, setTime] = useState("00:00");
  const availableTimes = ["00:00", "06:00", "12:00", "18:00"];
  const [items, setItems] = useState([
    { name: "Metal forks", count: 0 },
    { name: "Metal large spoons", count: 0 },
    { name: "Metal small spoons", count: 0 },
    { name: "Metal knives", count: 0 },
    { name: "Sharp knives", count: 0 },
    { name: "Large crockery plates", count: 0 },
    { name: "Small crockery plates", count: 0 },
    { name: "Crockery bowls", count: 0 },
    { name: "Crockery cups", count: 0 },
  ]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newItemName, setNewItemName] = useState("");
  const [newItemCount, setNewItemCount] = useState(0);
  const user = useSelector((state) => state.auth.user);
  const initialItemsRef = useRef(JSON.stringify(items));
  const today = new Date();
  const todayDate = today.toISOString().split("T")[0];

  const handleIncrement = (index) => {
    const updatedItems = [...items];
    updatedItems[index].count++;
    setItems(updatedItems);
  };

  const handleDecrement = (index) => {
    const updatedItems = [...items];
    if (updatedItems[index].count > 0) updatedItems[index].count--;
    setItems(updatedItems);
  };

  const handleDelete = (index) => {
    const updatedItems = items.filter((_, i) => i !== index);
    setItems(updatedItems);
  };

  const handleTimeChange = (selectedTime) => {
    setTime(selectedTime);
  };

  const handleDialogOpen = () => {
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setNewItemName("");
    setNewItemCount(0);
  };

  const handleAddNewItem = () => {
    if (newItemName.trim() && newItemCount >= 0) {
      setItems([...items, { name: newItemName, count: newItemCount }]);
      handleDialogClose();
    }
  };

  const handleCountChange = (e, index) => {
    const newValue = e.target.value;

    // Ensure the value is a valid number
    if (!isNaN(newValue) || newValue === "") {
      const updatedItems = [...items];
      updatedItems[index].count = newValue; // Update the specific item's count
      setItems(updatedItems); // Update state
    }
  };

  const handleSubmit = async () => {
    const requestBody = {
      items: items.map((item) => ({
        itemName: item.name,
        itemCount: item.count,
      })),
      time: time,
      created_by: user.username,
      created_at: todayDate, // Add today's date as created_at
    };

    try {
      await axios.post("/api/kitchens/kitchenItem", requestBody, {
        headers: { "Content-Type": "application/json", "x-user-role": user?.roleid },
      });
      onClose();
      setOpenDefaultSnackbar(true); // Notify success
    } catch (error) {
      console.error("Error submitting kitchen checklist:", error);

      // Check if the error is due to a duplicate time/created_at combination
      if (error.response && error.response.status === 409) {
        alert("Daily Kitchen Item Check List with the same time and date already exists.");
      }
    }
  };

  const isDoneDisabled = () => {
    return initialItemsRef.current === JSON.stringify(items); // Compare with initial state
  };

  return (
    <Box sx={{ p: 4, m: 2 }}>
      <Chip
        variant='contained'
        sx={{
          fontSize: "14px",
          backgroundColor: "#F2F6FF",
          color: "blue",
        }}
        label='Daily Tasks'
        size='small'
      />
      <Typography sx={{ fontSize: "24px", color: "black", fontWeight: "bold", mb: 2 }}>
        Daily Kitchen Item Check List
      </Typography>
      <Typography sx={{ fontSize: "14px", color: "#757575", mb: 3 }}>
        Date: {todayDate} <span style={{ color: "#1751D0" }}>• {user.username}</span>
      </Typography>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={10}>
          <Box sx={{ display: "flex", gap: 2 }}>
            {availableTimes.map((timeValue) => (
              <Button
                key={timeValue}
                variant={time === timeValue ? "contained" : "outlined"}
                onClick={() => handleTimeChange(timeValue)}
                sx={{
                  textTransform: "none",
                  borderRadius: "8px",
                }}
              >
                {timeValue}
              </Button>
            ))}
          </Box>
        </Grid>

        <Grid item xs={12} sm={2}>
          <Button variant='contained' onClick={handleDialogOpen}>
            Add Item
          </Button>
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {items.map((item, index) => (
          <Grid item xs={12} sm={6} key={index}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                border: "1px solid #ECECED",
                borderRadius: "8px",
                p: 1,
              }}
            >
              <Typography>{item.name}</Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <IconButton color='primary' size='small' onClick={() => handleDecrement(index)}>
                  <RemoveIcon />
                </IconButton>
                <TextField
                  value={item.count}
                  size='small'
                  onChange={(e) => handleCountChange(e, index)} // Add onChange event
                  inputProps={{
                    style: { textAlign: "center", width: "40px" },
                  }}
                />

                <IconButton color='primary' size='small' onClick={() => handleIncrement(index)}>
                  <AddIcon />
                </IconButton>

                <IconButton color='primary' size='small' onClick={() => handleDelete(index)}>
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
      <ReusableDialog
        isOpen={dialogOpen}
        onClose={handleDialogClose}
        title='Add A Item'
        onSubmit={handleAddNewItem}
        submitText='Submit'
        cancelText='Cancel'
      >
        <Typography sx={{ fontSize: "14px", color: "black" }}>Item Name</Typography>
        <TextField
          name='itemName'
          fullWidth
          variant='outlined'
          value={newItemName}
          onChange={(e) => setNewItemName(e.target.value)}
          sx={{ mb: 2 }}
        />

        <Typography sx={{ fontSize: "14px", color: "black", mb: 1 }}>Count Available</Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <TextField
            name='itemCount'
            value={newItemCount}
            size='small'
            type='number'
            inputProps={{ style: { textAlign: "center", width: "60px" } }}
            onChange={(e) => setNewItemCount(Number(e.target.value))}
          />
          <IconButton color='primary' onClick={() => setNewItemCount((prev) => Math.max(prev - 1, 0))}>
            <RemoveIcon />
          </IconButton>
          <IconButton color='primary' onClick={() => setNewItemCount((prev) => prev + 1)}>
            <AddIcon />
          </IconButton>
        </Box>
      </ReusableDialog>
      <Typography>
        All staff are to ensure there is the correct number of items always present within the home. Any items that are
        missing are to be looked for. Any concerns please inform the duty manager. Any damaged items or items that have
        been replaced; the home manager is to be informed.
      </Typography>
      <Grid item xs={12} mt={2}>
        <Grid container spacing={1} className='submit-button-container'>
          <Grid item>
            <Button
              variant='outlined'
              color='primary'
              className='submit-button'
              sx={{ textTransform: "none" }}
              onClick={() => onClose()}
            >
              Back
            </Button>
          </Grid>
          <Grid item>
            <Button
              type='submit'
              variant='contained'
              color='primary'
              className='submit-button'
              sx={{ textTransform: "none" }}
              onClick={handleSubmit}
              disabled={isDoneDisabled()} // Disable if no changes
            >
              Done
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ManagerKitchenChecklist;
